// Where the widget sits on the page.
//
// "right" and "left" are the two fixed corners. "custom" is a free point,
// stored as a percentage of the viewport (x from the left edge, y from the
// top) so one saved placement holds up across screen sizes - a point in
// pixels would drift to the middle of a laptop and off the edge of a phone.
//
// Shared by the customization preview and the install page's preview so the
// two can't disagree about what a saved placement looks like.

export const CUSTOM_POSITION = "custom";

// Far enough from the corner to read as deliberate, close enough that
// switching to Custom doesn't fling the widget across the page.
export const DEFAULT_CUSTOM_OFFSET = { x: 92, y: 88 };

export const isCustomPosition = (position) => position === CUSTOM_POSITION;

const clamp = (value, lo, hi) => Math.min(hi, Math.max(lo, value));

/** A stored offset, rounded and inside the viewport. 0-100, per axis. */
export const normalizeOffset = (value, fallback) => {
  const number = Number(value);
  return Number.isFinite(number)
    ? clamp(Math.round(number), 0, 100)
    : fallback;
};

/** The class that puts the corner placements on the correct side. */
export const alignClass = (position) => {
  if (isCustomPosition(position)) return "cz-align-custom";
  return position === "left" || position === "bottom-left"
    ? "cz-align-left"
    : "cz-align-right";
};

/**
 * Inline styles for a custom placement, one for the chat panel and one for
 * the launcher bubble.
 *
 * The bubble is centred on the point. The panel hangs off the point by its
 * nearest edge - left edge when the point is on the left half, right edge on
 * the right half, below the bubble when the point is in the top half and
 * above it when it is in the bottom half. That edge-anchoring is what keeps
 * the panel on screen at every point, including the very corners, which
 * centring it on the point would not.
 *
 * @param x percent from the left edge (0-100)
 * @param y percent from the top edge (0-100)
 * @param launcher the bubble's diameter in px, 0 where none is drawn
 * @param gap px between bubble and panel
 */
export const customPlacement = (x, y, { launcher = 42, gap = 10 } = {}) => {
  const fromLeft = x <= 50;
  const fromTop = y < 50;

  // Anchor to whichever pair of edges the point is nearest, so the offsets
  // behave like CSS insets rather than fighting the stage's own padding.
  const horizontal = fromLeft
    ? { left: `${x}%`, right: "auto" }
    : { right: `${100 - x}%`, left: "auto" };
  const vertical = fromTop
    ? { top: `${y}%`, bottom: "auto" }
    : { bottom: `${100 - y}%`, top: "auto" };

  const clear = launcher / 2 + gap;

  // The bubble is centred on the point by pulling its inset back by half its
  // width. max() stops that pull from taking it past the edge of the screen,
  // which is what a point in the very corner would otherwise do - the live
  // widget would be half off the page.
  const half = launcher / 2;
  const inset = (percent) =>
    half ? `max(0px, calc(${percent}% - ${half}px))` : `${percent}%`;

  return {
    panel: {
      ...horizontal,
      ...vertical,
      transform: `translateY(${fromTop ? clear : -clear}px)`,
    },
    launcher: fromLeft
      ? {
          left: inset(x),
          right: "auto",
          ...(fromTop
            ? { top: inset(y), bottom: "auto" }
            : { bottom: inset(100 - y), top: "auto" }),
        }
      : {
          right: inset(100 - x),
          left: "auto",
          ...(fromTop
            ? { top: inset(y), bottom: "auto" }
            : { bottom: inset(100 - y), top: "auto" }),
        },
  };
};

/**
 * Panel and launcher styles for any placement: the two corners return
 * undefined and stay on their stylesheet rules.
 */
export const placementStyles = (position, offset, options) =>
  isCustomPosition(position)
    ? customPlacement(
        normalizeOffset(offset?.x, DEFAULT_CUSTOM_OFFSET.x),
        normalizeOffset(offset?.y, DEFAULT_CUSTOM_OFFSET.y),
        options,
      )
    : { panel: undefined, launcher: undefined };

/**
 * Where a pointer landed, as a stored offset. Used by the drag in the
 * preview; the element box is what the pointer is measured against.
 */
export const offsetFromPointer = (event, rect) => ({
  x: clamp(Math.round(((event.clientX - rect.left) / rect.width) * 100), 0, 100),
  y: clamp(Math.round(((event.clientY - rect.top) / rect.height) * 100), 0, 100),
});
